import {
  memoizedGetProjectUserlist,
  projectUserlistCache,
  updateProjectUserList,
} from '@/services/collaboration';
import { useAsync } from '@/composables/useAsync';
import { computed, watch } from 'vue';
import { useSelectedProject } from '@/composables/useSelectedProject';
import type { Role, Member } from '@/types/collaboration';

export function useCollaboration() {
  const selectedProject = useSelectedProject();
  const projectId = computed(() => selectedProject.value?.id);

  const {
    data: members,
    fetch,
    ...rest
  } = useAsync(
    () =>
      projectId.value
        ? memoizedGetProjectUserlist('default', projectId.value)
        : Promise.resolve([]),
    [],
    {
      immediate: true,
    }
  );

  const refetch = async () => {
    projectUserlistCache.clear();
    await fetch();
  };

  watch(projectId, async () => {
    if (projectId.value) {
      await fetch();
    }
  });

  const totalMembers = computed(() => members.value?.length);
  const loading = computed(() => rest.loading.value);

  const removeMember = async (member: Member) => {
    if (!selectedProject.value) return;
    if (!member.role) return;

    await updateProjectUserList('default', selectedProject.value.id, {
      upsert: [],
      remove: [
        {
          id: member.id,
          role: member.role,
        },
      ],
    });

    await refetch();
  };

  const leaveProject = async (member: Member) => {
    if (!selectedProject.value) return;
    if (!member.role) return;

    await updateProjectUserList('default', selectedProject.value.id, {
      upsert: [],
      remove: [
        {
          id: member.id,
          role: member.role,
        },
      ],
    });
  };

  const updateMemberList = async (upsertMembers: { id: string; role: Role }[]) => {
    if (!selectedProject.value) return;
    await updateProjectUserList('default', selectedProject.value.id, {
      upsert: upsertMembers,
      remove: [],
    });

    await refetch();
  };

  return {
    members,
    ...rest,
    totalMembers,
    loading,
    fetch,
    removeMember,
    leaveProject,
    updateMemberList,
  };
}
